/**
 * Client for the CumulusVPN payments bridge (`bridge/`,
 * docs/18-payments-bridge.md) — the operator service that turns fiat
 * payments (Stripe card, Apple IAP, Google Play Billing) into on-chain FLUX
 * payments carrying the user's `CVPN1:<code>` memo.
 *
 * Unlike the gateway control API, bridge responses are NOT Ed25519-signed
 * (the bridge is a trusted operator service reached over HTTPS), so this
 * module uses plain fetch + the `{status,data}` envelope rather than
 * `fetchSigned`.
 */
import { ApiError } from './http.js';
/** Production bridge endpoint. Override via options for staging/tests. */
export const DEFAULT_BRIDGE_URL = 'https://pay.cumulusvpn.com';
async function bridgeFetch(fetchImpl, path, init, opts) {
    const base = opts?.baseUrl ?? DEFAULT_BRIDGE_URL;
    const timeoutMs = opts?.timeoutMs ?? 15_000;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetchImpl(`${base}${path}`, { ...init, signal: controller.signal });
        const parsed = (await res.json());
        if (parsed.status === 'error') {
            throw new ApiError(parsed.data);
        }
        if (parsed.status !== 'success' || parsed.data === undefined) {
            // Non-envelope body (proxy error page, gateway 502 JSON, …) — fail
            // loudly instead of returning undefined for callers to destructure.
            throw new Error(`bridge ${path}: unexpected response (HTTP ${res.status})`);
        }
        return parsed.data;
    }
    catch (err) {
        if (controller.signal.aborted) {
            throw new Error(`bridge ${path} timed out after ${timeoutMs / 1000}s`);
        }
        throw err;
    }
    finally {
        clearTimeout(timer);
    }
}
const postJson = (body) => ({
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
});
/**
 * Create a Stripe Checkout Session for a card subscription. Redirect the
 * user to the returned `url`; Stripe sends them back to the web upgrade
 * page, which then polls {@link paymentStatus}. An optional `voucher` (a
 * discount code previously validated via {@link redeemVoucher}) applies the
 * discount at checkout.
 */
export async function createStripeCheckout(fetchImpl, params, opts) {
    return bridgeFetch(fetchImpl, '/v1/stripe/checkout', postJson({
        payment_code: params.code,
        plan: params.plan,
        ...(params.voucher !== undefined ? { voucher: params.voucher } : {}),
    }), opts);
}
/**
 * Open a Stripe billing portal so a card subscriber can change their payment
 * method, switch plan, or cancel. Redirect the user to the returned `url`.
 *
 * `sessionId` is the Checkout Session id from the purchase
 * ({@link createStripeCheckout}, or the `?session=` the bridge redirects back
 * with) and it is what AUTHORIZES the portal — the payment code alone must
 * never be enough, because it is derived from the device pubkey that every
 * gateway a client enrolls with receives. Callers should persist the session
 * id locally at purchase time; there is no account to recover it from.
 *
 * Throws {@link ApiError} with slug `no_subscription` when the session is
 * unknown, has no customer, or belongs to a different payment code.
 */
export async function openBillingPortal(fetchImpl, params, opts) {
    return bridgeFetch(fetchImpl, '/v1/stripe/portal', postJson({ payment_code: params.code, session_id: params.sessionId }), opts);
}
/**
 * Redeem a voucher / promo code for this device's payment code.
 *
 * `grant_days` outcomes are consumed immediately — the bridge queues the
 * on-chain settlement and {@link paymentStatus} tracks it ("activating…").
 * `stripe_discount` outcomes are NOT consumed: pass the same code as
 * `voucher` to {@link createStripeCheckout} to apply it. Errors surface as
 * {@link ApiError} with slugs `invalid` / `expired` / `exhausted` /
 * `already_redeemed` / `temporarily_unavailable`.
 */
export async function redeemVoucher(fetchImpl, params, opts) {
    return bridgeFetch(fetchImpl, '/v1/voucher/redeem', postJson({ payment_code: params.code, code: params.voucher }), opts);
}
/**
 * Verify an Apple StoreKit 2 purchase. `signedTransaction` is the JWS from
 * the purchase result; the bridge checks its signature chain AND that its
 * `appAccountToken` equals `appAccountToken(code)` before granting.
 */
export async function verifyApplePurchase(fetchImpl, params, opts) {
    return bridgeFetch(fetchImpl, '/v1/apple/verify', postJson({ payment_code: params.code, signed_transaction: params.signedTransaction }), opts);
}
/** Verify a Google Play Billing purchase token server-side. */
export async function verifyGooglePurchase(fetchImpl, params, opts) {
    return bridgeFetch(fetchImpl, '/v1/google/verify', postJson({ payment_code: params.code, purchase_token: params.purchaseToken }), opts);
}
/**
 * Recent bridge payments for a code — drives the "Activating…" UX between
 * a fiat payment and the gateways seeing the confirmed chain tx.
 */
export async function paymentStatus(fetchImpl, code, opts) {
    return bridgeFetch(fetchImpl, `/v1/payment/${encodeURIComponent(code)}/status`, undefined, opts);
}
//# sourceMappingURL=bridge.js.map